'use client';

import { useCallback, useRef } from 'react';
import type {
  ChangeEvent,
  FocusEvent,
  KeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';

export interface UseSearchFieldOptions {
  /** Aktueller Wert des Feldes. */
  value: string;
  /** Tastatureingaben — setzt nur den lokalen Wert (darf debounced sein). */
  onChange: (value: string) => void;
  /**
   * Sofortige Übernahme inklusive lokalem Wert — für ✕ und Escape. Löschen
   * soll sich nicht verzögert anfühlen, deshalb umgeht dieser Pfad den
   * Debounce der Aufrufstelle.
   */
  onCommit: (value: string) => void;
  /**
   * Markiert beim Fokus von außen den ganzen Begriff (Adressleisten-Regel).
   * Auf false setzen, falls sich das Verhalten als störend erweist.
   */
  selectOnRefocus?: boolean;
}

export interface UseSearchFieldResult {
  inputProps: {
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
    onPointerDown: (event: ReactPointerEvent<HTMLInputElement>) => void;
    onPointerUp: (event: ReactPointerEvent<HTMLInputElement>) => void;
    onPointerCancel: (event: ReactPointerEvent<HTMLInputElement>) => void;
    onFocus: (event: FocusEvent<HTMLInputElement>) => void;
    onBlur: () => void;
  };
  /** Steuert die Sichtbarkeit des ✕. */
  hasValue: boolean;
  /** Leert das Feld, meldet sofort nach außen und fokussiert zurück. */
  clear: () => void;
}

/**
 * Gemeinsames Verhalten der CRM-Suchfelder: Escape leert, ✕ leert, und ein
 * Fokus von außen markiert den bisherigen Begriff, damit man ihn direkt
 * überschreiben kann.
 *
 * Das Markieren hängt bewusst an den Pointer-Ereignissen statt an `onFocus`:
 * `onFocus` allein würde auch beim zweiten Klick ins bereits fokussierte Feld
 * markieren (dann kann niemand mehr einen Cursor setzen) und eine gezogene
 * Teilauswahl sofort wieder zerstören.
 */
export function useSearchField({
  value,
  onChange,
  onCommit,
  selectOnRefocus = true,
}: UseSearchFieldOptions): UseSearchFieldResult {
  // Zuletzt berührtes Element. Die Belegsuche spreizt diese Props auf zwei
  // Inputs, die beide gleichzeitig im DOM stehen (nur per CSS ausgeblendet) —
  // eine einzelne Ref würde immer auf den zuletzt gerenderten zeigen.
  const lastInputRef = useRef<HTMLInputElement | null>(null);
  // Zwischen pointerdown und pointerup: soll dieser Klick markieren?
  const pendingSelectRef = useRef(false);
  // Pointer-Typ des letzten pointerdown, solange die Interaktion "offen"
  // ist (noch kein blur/pointercancel). Ersetzt eine reine "kam von
  // Pointer"-Flagge: Maus/Pen vergeben den Fokus NACH pointerdown, VOR
  // pointerup (pointerdown → focus → pointerup) — Touch-Geräte dagegen
  // erst NACH pointerup (pointerdown → pointerup → focus), damit ein auf
  // dem Feld beginnendes Scrollen nicht die Tastatur aufreisst. Ein
  // Boolean, das in pointerup zurückgesetzt wird, wäre beim Touch-Ablauf
  // schon wieder false, sobald focus eintrifft, und würde fälschlich wie
  // Tastaturfokus behandelt (→ select() trotz Touch). Deshalb: Reset nur
  // in blur/pointercancel, nie in pointerup.
  const pointerTypeRef = useRef<string | null>(null);
  // Art der letzten Berührung des Felds — überlebt wie lastInputRef bewusst
  // den blur: Der ✕-Knopf nimmt dem Feld beim Antippen den Fokus, blur läuft
  // also VOR clear(). Nur so weiß clear() noch, ob zuletzt ein Finger im
  // Spiel war. pointerTypeRef taugt dafür nicht, es wird in blur geleert.
  const lastPointerTypeRef = useRef<string | null>(null);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    onCommit('');
    // Auf Touch KEIN Rückfokus: Das Feld wieder zu fokussieren reisst die
    // Bildschirmtastatur auf, obwohl das ✕ dort meist "zeig mir die Liste
    // wieder" heisst — und verdeckt dann genau die Liste. Dieselbe Erwägung
    // wie beim Select-all, das auf Touch ebenfalls unterbleibt.
    // Mit Maus/Tastatur bleibt der Cursor im Feld, man kann weitertippen.
    // Bei Escape ist der Fokus ohnehin schon im Feld, der Aufruf also ein
    // No-op — die Ausnahme kostet dort nichts.
    if (lastPointerTypeRef.current === 'touch') return;
    lastInputRef.current?.focus();
  }, [onCommit]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== 'Escape') return;
      // Im leeren Feld nicht abfangen: dann darf Escape weiterreichen und
      // z.B. einen umgebenden Dialog schliessen.
      if (value.length === 0) return;
      event.preventDefault();
      event.stopPropagation();
      clear();
    },
    [clear, value],
  );

  const handlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      lastInputRef.current = input;
      pointerTypeRef.current = event.pointerType;
      lastPointerTypeRef.current = event.pointerType;
      pendingSelectRef.current =
        selectOnRefocus &&
        event.pointerType !== 'touch' &&
        document.activeElement !== input;
    },
    [selectOnRefocus],
  );

  const handlePointerUp = useCallback(
    (event: ReactPointerEvent<HTMLInputElement>) => {
      if (!pendingSelectRef.current) return;
      pendingSelectRef.current = false;
      const input = event.currentTarget;
      // Wurde gezogen, gewinnt die gezogene Auswahl.
      if (input.selectionStart !== input.selectionEnd) return;
      input.select();
    },
    [],
  );

  const handleFocus = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      lastInputRef.current = event.currentTarget;
      // Fokus im Rahmen einer noch offenen Pointer-Interaktion: bei
      // Maus/Pen entscheidet pointerup (nach der Zieh-Prüfung); bei Touch
      // wird NIE markiert (s. Kommentar an pointerTypeRef) — in beiden
      // Fällen hier also nichts tun.
      if (pointerTypeRef.current !== null) return;
      if (!selectOnRefocus) return;
      event.currentTarget.select();
    },
    [selectOnRefocus],
  );

  // Beendet eine Pointer-Interaktion ohne Auswahl-Konsequenz: regulärer
  // Fokusverlust (blur) oder ein abgebrochener Pointer (pointercancel —
  // z.B. Touch-Scroll, der auf dem Feld beginnt und nie einen Fokus
  // auslöst). Ohne diesen Reset bliebe pointerTypeRef auf 'touch' hängen
  // und der nächste echte Tastaturfokus würde fälschlich nicht markieren.
  const resetPointerState = useCallback(() => {
    pendingSelectRef.current = false;
    pointerTypeRef.current = null;
  }, []);

  return {
    inputProps: {
      value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onPointerDown: handlePointerDown,
      onPointerUp: handlePointerUp,
      onPointerCancel: resetPointerState,
      onFocus: handleFocus,
      onBlur: resetPointerState,
    },
    hasValue: value.length > 0,
    clear,
  };
}
